import { COLORING_GRID_STATUS_OPTIONS, COLORING_GRID_STATUSES } from 'app-constants/coloringGrids';
import {
  collection,
  DocumentSnapshot,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  OrderByDirection,
  query,
  QueryConstraint,
  QueryDocumentSnapshot,
  startAfter,
  where,
} from 'firebase/firestore';
import { useColoringGridCollections } from 'hooks/useColoringGridCollections';
import { DashboardRoutePath } from 'modules/dashboard/navigation/path';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { SingleValue } from 'react-select';
import { Column } from 'react-table';
import { StatusLabel } from 'shared/Components/StatusLabel';
import { SortOder, Status } from 'shared/enums';
import styled from 'styled-components';
import { buildLookupObject, capitalize } from 'utilities';

import { getArtists } from '../../../../../services';
import { firestore } from '../../../../../services/Firebase';
import { Button, Select, Splash } from '../../../../../shared/Components';
import ColoringGridImage from '../../../../../shared/Components/ColoringGridImage/ColoringGridImage';
import { Option } from '../../../../../shared/Components/Select/Select';
import { ActionButtons, TableComponent } from '../../../../../shared/Components/Table/Table';
import { FlexContainer, Image, Text } from '../../../../../shared/Typhography';
import { strings } from '../../../../../strings';
import { ColoringGridDTO } from '../../../../../types/DTO/ColoringGridDTO';
import { FilterReducerAction, FilterReducerState } from '../ColoringGridsScreen';

import { AIIcon } from './AIIcon';
import { DeleteButton } from './DeleteButton';

interface Data {
  index: number;
  firebaseId: string;
  icon: string;
  title: string;
  artist: QueryDocumentSnapshot;
  status: string;
  premiumTier: number;
  collectionName: string;
  statusItem: string;
  keywords: string;
  aiGenerated: boolean;
  collections?: string[];
  createdAt: string;
  type?: 'static' | 'video';
  action: () => void;
  updateOrApproveColoringGridUrl: string;
  __onRowClick__?: () => void;
}

interface ColoringGridTableProps {
  filterState: [FilterReducerState, React.Dispatch<FilterReducerAction>];
}

enum SortKeys {
  CREATED_AT = 'createdAt',
  PREMIUM_TIER = 'premiumTier',
}

type SortTypes = {
  key: SortKeys.CREATED_AT | SortKeys.PREMIUM_TIER;
  direction: SortOder.Ascending | SortOder.Descending;
};

const NullText = styled.p`
  color: ${(props) => props.theme.colors.red};
  ${(props) => props.theme.typography.fontBold}
`;

const StyledTableComponent = styled(TableComponent)`
  th,
  td {
    text-align: start;
  }
`;

const filterFieldMapping: { [keys in keyof FilterReducerState]: string } = {
  collection: 'collections',
  artist: 'publisher',
  aiGenerated: 'aiGenerated',
  status: 'status',
  premiumTier: 'premiumTier',
};

// Select component static options.
const trueFalseOptions = [
  {
    value: true,
    label: (
      <FlexContainer margin="0 0 0 10px" alignItems="center">
        <AIIcon value={true} />
      </FlexContainer>
    ),
  },
  {
    value: false,
    label: (
      <FlexContainer margin="0 0 0 10px" alignItems="center">
        <AIIcon value={false} />
      </FlexContainer>
    ),
  },
];

const COLORING_GRID_TIER_OPTIONS = [
  { value: 0, label: 'Free' },
  { value: 1, label: 'Premium' },
];

const translations = strings.pages.coloringGridsScreen;

export const ColoringGridTable = ({ filterState }: ColoringGridTableProps) => {
  const [filters, filterDispatch] = filterState;
  const [artists, setArtists] = React.useState<any>([]);
  const [collections, setCollections] = React.useState<Option[]>([]);
  const [coloringGrids, setColoringGrids] = React.useState<DocumentSnapshot[] | undefined>(
    undefined,
  );
  const coloringGridsLastDoc = React.useRef<DocumentSnapshot | null>(null);
  const [numberOfColoringGrids, setNumberOfColoringGrids] = React.useState<undefined | number>();
  const [loadingMore, setLoadingMore] = React.useState<boolean>(false);
  const { data: artistsData, isLoading: isLoadingArtist } = getArtists();
  const { docs: collectionData, state: collectionsFetchState } = useColoringGridCollections();

  const [sort, setSort] = useState<SortTypes>({
    key: SortKeys.CREATED_AT,
    direction: SortOder.Descending,
  });

  const [isReady, setReady] = useState<boolean>(false);

  // Artist-Publisher map for coloring grid table data.
  const artistsMap = useMemo(
    () =>
      artistsData
        ? (() => {
            setReady(true);
            return buildLookupObject(artistsData.docs, 'id');
          })()
        : {},
    [artistsData, isLoadingArtist],
  );
  const previousArtistDataLength = useRef<number>(0);

  const queryConstraints = useMemo(() => {
    const constraints: Array<QueryConstraint> = [];

    for (const [key, value] of Object.entries(filters)) {
      // isAllPremiumFilterActive = key === SortKeys.PREMIUM_TIER && value === -1;
      if (value !== undefined && value !== null && value !== 'ALL') {
        constraints.push(
          where(
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            filterFieldMapping[key],
            key === 'collection' ? 'array-contains' : '==',
            value,
          ),
        );
      }
    }

    // If status is not selected, search status then "COLORING_GRID_STATUSES.approved" or "COLORING_GRID_STATUSES.pending" by default.
    if (!filters.status) {
      constraints.push(
        where('status', 'in', [
          COLORING_GRID_STATUSES.approved,
          COLORING_GRID_STATUSES.pending,
          COLORING_GRID_STATUSES.edited,
        ]),
      );
    }

    return constraints;
  }, [filters]);

  useEffect(() => {
    fetchMore(true);
  }, [
    filters.artist,
    filters.collection,
    filters.aiGenerated,
    filters.status,
    filters.premiumTier,
    sort,
    isReady,
  ]);

  const fetchMore = useCallback(
    async (reset = false) => {
      // When search by image filter is active, there is no need to fetch any other data
      // until the filter is cleared.
      if (loadingMore || !isReady) {
        return;
      }

      setLoadingMore(true);

      if (reset) {
        setNumberOfColoringGrids(
          (
            await getCountFromServer(
              query(collection(firestore, 'coloringGrids'), ...queryConstraints),
            )
          ).data().count ?? 0,
        );
      }

      const coloringGridsQuery = !reset
        ? query(
            collection(firestore, 'coloringGrids'),
            ...queryConstraints,
            orderBy(sort.key, sort.direction as OrderByDirection),
            startAfter(coloringGridsLastDoc.current ?? undefined),
            limit(20),
          )
        : query(
            collection(firestore, 'coloringGrids'),
            ...queryConstraints,
            orderBy(sort.key, sort.direction as OrderByDirection),
            limit(20),
          );

      const coloringGridsSnapshot = await getDocs(coloringGridsQuery);
      const lastColoringGridIndex = reset ? 0 : coloringGrids?.length ?? 0;

      const coloringGridsTableData = coloringGridsSnapshot?.docs?.map(
        (coloringGridSnapshot, index) => {
          const coloringGridData = coloringGridSnapshot.data() as ColoringGridDTO;

          const updateOrApproveColoringGridUrl = artistsMap[coloringGridData.publisher]?.data()
            .requiresInitialApproval
            ? `/${DashboardRoutePath.FIRST_APPROVAL}/${artistsMap[coloringGridData.publisher].id}`
            : `/${DashboardRoutePath.MANAGE_COLORING_GRIDS}/${coloringGridSnapshot.id}`;

          return {
            index: lastColoringGridIndex + 1 + index,
            id: coloringGridSnapshot.id,
            firebaseId: coloringGridSnapshot.id,
            icon: coloringGridData.iconUrl,
            title: coloringGridData.name,
            artist: artistsMap[coloringGridData.publisher],
            statusItem: coloringGridData.status,
            keywords: coloringGridData.keywords,
            collections: coloringGridData.collections,
            aiGenerated: coloringGridData.aiGenerated,
            premiumTier: coloringGridData.premiumTier ?? 0,
            updateOrApproveColoringGridUrl,
            createdAt:
              coloringGridData.createdAt
                .toDate()
                .toLocaleString('en-us', { month: 'short', year: 'numeric', day: 'numeric' }) ||
              '/',
            status: coloringGridData.status || 'none',
            //Todo: Use enums from DashboardRoutePath. We have some imports problem atm. Need to resolve that first.
            __onRowClick__: () => window.open(updateOrApproveColoringGridUrl),
          };
        },
      );

      setColoringGrids((curr: any) =>
        curr && !reset ? [...curr, ...coloringGridsTableData] : coloringGridsTableData,
      );

      if (coloringGridsSnapshot?.docs?.length) {
        coloringGridsLastDoc.current =
          coloringGridsSnapshot.docs[coloringGridsSnapshot.docs.length - 1];
      } else {
        coloringGridsLastDoc.current = null;
      }

      setLoadingMore(false);
    },
    [
      loadingMore,
      setLoadingMore,
      setNumberOfColoringGrids,
      setColoringGrids,
      queryConstraints,
      sort,
      artistsMap,
      coloringGrids,
      isReady,
    ],
  );

  const toggleSort = (key: SortKeys.CREATED_AT | SortKeys.PREMIUM_TIER) => {
    setSort((prevState) => ({
      key,
      direction:
        prevState.key === key
          ? prevState.direction === SortOder.Descending
            ? SortOder.Ascending
            : SortOder.Descending
          : SortOder.Descending,
    }));
  };

  const getArtistsData = () => {
    const finalData = artistsData?.docs
      ?.filter((item) => item.data().Name || item.data().username)
      ?.map((item) => ({
        value: item.id,
        label: (
          <FlexContainer gap="1rem" alignItems="center" flexDirection="row">
            <Image
              size="profileImage"
              borderRadiusType="circle"
              src={item.data().profileImageUrl}
            />
            {`${item.data().Name} (${item.data().username})`}
          </FlexContainer>
        ),
        searchValue: `${item.data().Name} ${item.data().username}`, // For simpler custom search functionality.
      }));
    setArtists(finalData);
  };

  useEffect(() => {
    if (artistsData?.docs && previousArtistDataLength.current !== artistsData?.docs?.length) {
      previousArtistDataLength.current = artistsData?.docs?.length;
      getArtistsData();
    }
  }, [artistsData]);

  const getCollectionsData = () => {
    const finalData = collectionData.map((item) => ({
      value: item.id,
      label: item.data().name,
    }));

    setCollections(finalData);
  };

  const headers: Column<Data>[] = React.useMemo(
    () => [
      {
        Header: '#',
        accessor: 'index',
        Cell: (cell) => {
          return <FlexContainer width="20px">{cell.row.original.index}</FlexContainer>;
        },
        disableSortBy: true,
        width: '3%',
      },
      {
        Header: translations.tableHeaders.icon,
        accessor: 'icon',
        Cell: (cell) => {
          return (
            <ColoringGridImage
              src={cell.row.original.icon || ''}
              loading="lazy"
              style={{
                width: '64px',
                borderRadius: '15px',
              }}
            />
          );
        },
        disableSortBy: true,
        width: '5%',
      },
      {
        Header: translations.tableHeaders.name,
        accessor: 'title',
        Cell: (cell) => {
          return (
            <Text
              style={{ width: (window.screen.width * 10) / 100 }}
              padding="0 0 0 4px"
              ellipsisOnRow={1}
              title={cell.row.original.title}
            >
              {cell.row.original.title}
            </Text>
          );
        },
        disableSortBy: true,
        width: '15%',
      },
      {
        Header: translations.tableHeaders.author,
        accessor: 'artist',
        Cell: (cell) => {
          const artistName = `${cell.row.original.artist?.data().Name} (${
            cell.row.original.artist?.data().username
          })`;

          return (
            <Text
              style={{ width: 180 }}
              padding="0 4px"
              ellipsisOnRow={1}
              onClick={(e) => e.stopPropagation()}
            >
              {cell.row.original.artist?.exists() ? (
                <a
                  href={`/view-artist/${cell.row.original.artist?.id}`}
                  title={artistName}
                  target="_blank"
                  rel="noreferrer"
                >
                  {artistName}
                </a>
              ) : (
                'not-defined'
              )}
            </Text>
          );
        },
        disableSortBy: true,
        width: '10%',
        maxWidth: 200,
      },

      {
        Header: translations.tableHeaders.status,
        accessor: 'status',
        Cell: (cell) => {
          const key = capitalize(cell?.row?.original?.status || 'None');
          return <StatusLabel value={Status[key as keyof typeof Status]} />;
        },
        disableSortBy: true,
        width: '7.5%',
      },
      {
        Header: translations.tableHeaders.createdAt,
        accessor: 'createdAt',
        Cell: (cell) => <b>{cell.row.original.createdAt}</b>,
        width: '10%',
        onHeaderClick: () => toggleSort(SortKeys.CREATED_AT),
      },
      {
        Header: translations.tableHeaders.categories,
        accessor: 'collections',
        Cell: (cell) => {
          return (
            <div style={{ paddingLeft: 5, width: (window.screen.width * 15) / 100 }}>
              {collections && collections?.length > 0 ? (
                cell?.row?.original?.collections?.map((category, i) => (
                  <Text
                    key={`collection-${category}-${i}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      filterDispatch({
                        type: 'COLLECTION',
                        value: category,
                      });
                    }}
                    ellipsisOnRow={1}
                  >
                    {collections.find((clc: Option) => clc.value === category)?.label}
                  </Text>
                ))
              ) : (
                <NullText>No Categories</NullText>
              )}
            </div>
          );
        },
        disableSortBy: true,
        width: '20%',
      },
      {
        Header: translations.tableHeaders.price,
        accessor: 'premiumTier',
        Cell: (cell) => {
          let tier = cell?.row?.original?.premiumTier;
          if (isNaN(tier)) tier = 0;
          return (
            <FlexContainer margin="0 0 0 10px" alignItems="center">
              <div
                style={{
                  borderRadius: '50%',
                  background: tier === 0 ? '#A3A8AE' : '#585CEA',
                  width: 22,
                  height: 22,
                  textAlign: 'center',
                }}
              >
                <Text size={0.85} top="2px" color="#fff">
                  {String(tier)}
                </Text>
              </div>
            </FlexContainer>
          );
        },
        disableSortBy: false,
        width: '10%',
        onHeaderClick: () => toggleSort(SortKeys.PREMIUM_TIER),
      },
      {
        Header: translations.tableHeaders.byAi,
        accessor: 'aiGenerated',
        Cell: (cell) => {
          return (
            <FlexContainer
              style={{ paddingLeft: 5 }}
              alignItems="flex-start"
              justifyContent="flex-start"
            >
              <AIIcon value={!!cell.row.original.aiGenerated} />
            </FlexContainer>
          );
        },
        disableSortBy: true,
        width: '10%',
      },
      {
        Header: translations.tableHeaders.action,
        accessor: 'action',
        Cell: (cell) => {
          const status = cell.row.original.statusItem;

          const canDelete =
            status === COLORING_GRID_STATUSES.approved ||
            status === COLORING_GRID_STATUSES.edited ||
            status === COLORING_GRID_STATUSES.pending ||
            status === COLORING_GRID_STATUSES.rejected ||
            status === COLORING_GRID_STATUSES.disabled;

          const canEdit = status !== COLORING_GRID_STATUSES.deleted;

          return (
            <ActionButtons onClick={(e) => e.stopPropagation()}>
              {canDelete ? (
                // TODO #2uabepv
                // onCompleted callback is artificial and should be removed, as
                // ColoringGridTable shall subscribe to firebase collection changes and update automatically.
                <DeleteButton
                  id={cell.row.original.firebaseId}
                  onCompleted={async () => {
                    await fetchMore(true);
                  }}
                />
              ) : (
                <div style={{ width: 100 }} />
              )}
              <NavLink to={cell.row.original.updateOrApproveColoringGridUrl}>
                {/* Above route edit-coloring-grid need to be in brackets ENUM but i got some type error Need to be fixed */}
                <Button disabled={!canEdit} variant="link" type="button">
                  {strings.edit}
                </Button>
              </NavLink>
              <NavLink
                to={
                  cell.row.original.artist?.data().requiresInitialApproval &&
                  status !== COLORING_GRID_STATUSES.deleted
                    ? `/view-artist/${cell.row.original.artist.id}`
                    : `/view-coloring-grid/${cell.row.original.firebaseId}`
                }
              >
                {/* Above route edit-coloring-grid need to be in brackets ENUM but i got some type error Need to be fixed */}
                <Button variant="link">View</Button>
              </NavLink>
              <NavLink to={`/revenue-by-coloring-grid/${cell.row.original.firebaseId}`}>
                <Button variant="link">Revenue</Button>
              </NavLink>
            </ActionButtons>
          );
        },
        disableSortBy: true,
      },
    ],
    [coloringGrids, artistsMap],
  );

  React.useEffect(() => {
    if (collectionsFetchState === 'loading') return;
    if (collectionsFetchState === 'error') return;

    getCollectionsData();
  }, [collectionsFetchState]);

  if (!coloringGrids) {
    return <Splash />;
  }

  return (
    <>
      <FlexContainer gap="1rem" flexWrap="wrap">
        <Text weight="bold">Number of coloring grids:</Text>{' '}
        {numberOfColoringGrids ?? <Splash width={16} />}
      </FlexContainer>
      <FlexContainer gap="1rem" flexWrap="wrap">
        <Select
          id="coloring-grid-select-artist"
          label="Artist filter"
          onChange={(e) => filterDispatch({ type: 'ARTIST', value: e?.value.toString() })}
          options={[...artists]}
          value={artists?.find((itm: SingleValue<Option>) => itm?.value == filters.artist)}
          style={{ maxWidth: '250px', minWidth: '200px' }}
          isClearable={true}
          includeCustomSearch
        />

        <Select
          id="coloring-grid-select-collection"
          label="Collection filter"
          onChange={(e) => filterDispatch({ type: 'COLLECTION', value: e?.value.toString() })}
          options={[...collections]}
          value={collections?.find((itm: SingleValue<Option>) => itm?.value == filters.collection)}
          isClearable={true}
          style={{ maxWidth: '250px', minWidth: '200px' }}
        />

        <Select
          id="coloring-grid-select-ai"
          label="By AI filter"
          onChange={(e) =>
            filterDispatch({
              type: 'AI_GENERATED',
              value: typeof e?.value === 'boolean' ? e.value : null,
            })
          }
          options={trueFalseOptions}
          value={trueFalseOptions.find(
            (itm: SingleValue<Option>) => itm?.value == filters.aiGenerated,
          )}
          isClearable={true}
          style={{ width: '120px' }}
        />

        <Select
          id="coloring-grid-select-by-status"
          label="By status"
          onChange={(e) =>
            filterDispatch({
              type: 'STATUS',
              value: e?.value?.toString(),
            })
          }
          options={COLORING_GRID_STATUS_OPTIONS}
          value={COLORING_GRID_STATUS_OPTIONS.find(
            (itm: SingleValue<Option>) => itm?.value == filters.status,
          )}
          isClearable={true}
          style={{ width: '120px' }}
        />

        <Select
          id="coloring-grid-premuim-tier-select"
          label="Price"
          onChange={(e) =>
            filterDispatch({
              type: 'PREMIUMTIER',
              value: typeof e?.value === 'undefined' ? null : e?.value,
            })
          }
          options={COLORING_GRID_TIER_OPTIONS}
          value={COLORING_GRID_TIER_OPTIONS.find(
            (item: SingleValue<Option>) => item?.value == filters.premiumTier,
          )}
          style={{ width: '120px' }}
          isClearable={true}
        />
      </FlexContainer>

      {!coloringGrids.length ? (
        <Text size={1.2} textAlign="center">
          {translations.tableContent.noContent}
        </Text>
      ) : (
        <StyledTableComponent
          loadingMore={loadingMore}
          fetchMore={fetchMore}
          columns={headers}
          data={coloringGrids}
        />
      )}
    </>
  );
};
